import React, { useEffect, useState } from "react";
import { ButtonGroup, Card, Col, Dropdown, Row } from "react-bootstrap";
import { WellBeingLineChart } from "../report-ui/WellBeingLineChart";
import { fetchReportAssessments, fetchAssessmentTypes } from "../../services/httpClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import html2canvas from "html2canvas";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { CSVLink } from "react-csv";
import ReportTimeInterval from "./ui/ReportTimeInterval";
import { signal } from "@preact/signals-react";

export const isWellBeing = signal(true);

const WellBeingTab = ({ buildPDF, canvas, setCanvas, iPad }: any) => {
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [dateInterval, setDateInterval] = useState<number>(10000);

  // const [assessData, setAssessData] = useState();
  const [satisfaction, setSatisfaction] = useState<any>();
  const [health, setHealth] = useState<any>();
  const [purpose, setPurpose] = useState<any>();
  const [virtue, setVirtue] = useState<any>();
  const [social, setSocial] = useState<any>();
  const [stability, setStability] = useState<any>();
  const [trend, setTrend] = useState<any>();
  const [fullTableData, setFullTableData] = useState<any>();

  const [reportRunning, setReportRunning] = useState<boolean>(false);

  const fullTableHeaders = [
    { label: "Assessment Date", key: "date" },
    { label: "Case ID", key: "case_id" },
    { label: "Case Name", key: "case_name" },
    { label: "Assessment ID", key: "assessment_id" },
    { label: "Assessment Name", key: "assessment_name" },

    { label: "Composite", key: "composite" },

    //Happiness and Life Satisfaction
    { label: "Question 1", key: "question1" },
    { label: "Value 1", key: "1" },
    { label: "Question 2", key: "question2" },
    { label: "Value 2", key: "2" },

    //Mental and Physical Health
    { label: "Question 3", key: "question3" },
    { label: "Value 3", key: "3" },
    { label: "Question 4", key: "question4" },
    { label: "Value 4", key: "4" },

    //Meaning and Purpose
    { label: "Question 5", key: "question5" },
    { label: "Value 5", key: "5" },
    { label: "Question 6", key: "question6" },
    { label: "Value 6", key: "6" },

    //Character and Virtue
    { label: "Question 7", key: "question7" },
    { label: "Value 7", key: "7" },
    { label: "Question 8", key: "question8" },
    { label: "Value 8", key: "8" },

    //Close Social Relationships
    { label: "Question 9", key: "question9" },
    { label: "Value 9", key: "9" },
    { label: "Question 10", key: "question10" },
    { label: "Value 10", key: "10" },

    //Financial and Material Stability
    { label: "Question 11", key: "question11" },
    { label: "Value 11", key: "11" },
    { label: "Question 12", key: "question12" },
    { label: "Value 12", key: "12" },
  ];

  useEffect(() => {
    const start = new Date();
    start.setDate(start.getDate() - dateInterval);
    setStartDate(start.toISOString().slice(0, 10));
    setEndDate(new Date().toISOString().slice(0, 10));
    getWellBeingData(start.toISOString().slice(0, 10), new Date().toISOString().slice(0, 10));
  }, []);

  useEffect(() => {
    if (reportRunning) {
      buildPDF();
      setReportRunning(false);
    }
  }, [canvas]);

  const getWellBeingData = (start: string, end: string) => {
    let assessmentType: any;
    // get well being assessment id
    fetchAssessmentTypes().then((res: any) => {
      assessmentType = res[0].data.find((item: any) => item.key === "well_being");
      // get data for well being assessment
      fetchReportAssessments(assessmentType.id, start, end)
        .then((res: any) => {
          const well = res[0].data.map((item: any) => ({
            ...item,
            value: parseInt(item.value, 10),
          }));
          isWellBeing.value = well.length > 0 ? true : false;
          cleanFullTableData(well);
          calculateCategories(well);
          calculateWellBeingTrend(well);
        })
        .catch((err: any) => {
          console.log(err);
        });
    });
  };

  const cleanFullTableData = (data: any) => {
    let tdata: any[] = [];
    // Group data by id
    const grouped: { [assessment_id: string]: any[] } = {};
    data.forEach((item: any) => {
      if (!grouped[item.assessment_id]) grouped[item.assessment_id] = [];
      grouped[item.assessment_id].push(item);
    });
    Object.values(grouped).forEach((group: any[]) => {
      // Each group should represent a single assessment instance
      const row: any = {};
      const first = group[0] || {};
      row.date = first.assessment_at || "";
      row.case_name = first.case_name || "";
      row.case_id = first.case_id || "";
      row.assessment_id = first.assessment_id || "";
      row.assessment_name = first.assessment_name || "";

      let sum = 0;
      for (const item of group) {
        if (item.order) {
          row["question" + item.order] = item.question ? item.question : "";
          row[item.order] = item.value || 0;
          sum += item.value || 0;
        }
      }
      row.composite = sum / group.length || 0;
      row.composite = parseFloat(row.composite.toFixed(1)); // Round to 1 decimal place
      tdata.push(row);
    });
    setFullTableData(tdata);
  };

  const calculateCategories = (data: any) => {
    var runningValue = 0;
    //Happiness and Life Satisfaction
    var working = data.filter((i: any) => i.category === "Happiness and Life Satisfaction");
    if (working?.length > 0) {
      working?.map((w: any) => {
        runningValue = w?.value + runningValue;
      });
      var value = runningValue / working?.length;
      setSatisfaction(parseFloat(value.toFixed(1)));
    } else setSatisfaction("-");

    runningValue = 0;
    //Mental and Physical Health
    working = data.filter((i: any) => i.category === "Mental and Physical Health");
    if (working?.length > 0) {
      working?.map((w: any) => {
        runningValue = w?.value + runningValue;
      });
      value = runningValue / working?.length;
      setHealth(parseFloat(value.toFixed(1)));
    } else setHealth("-");

    runningValue = 0;
    //Meaning and Purpose
    working = data.filter((i: any) => i.category === "Meaning and Purpose");
    if (working?.length > 0) {
      working?.map((w: any) => {
        runningValue = w?.value + runningValue;
      });
      value = runningValue / working?.length;
      setPurpose(parseFloat(value.toFixed(1)));
    } else setPurpose("-");

    runningValue = 0;
    //Character and Virtue
    working = data.filter((i: any) => i.category === "Character and Virtue");
    if (working?.length > 0) {
      working?.map((w: any) => {
        runningValue = w?.value + runningValue;
      });
      value = runningValue / working?.length;
      setVirtue(parseFloat(value.toFixed(1)));
    } else setVirtue("-");

    runningValue = 0;
    //Close Social Relationships
    working = data.filter((i: any) => i.category === "Close Social Relationships");
    if (working?.length > 0) {
      working?.map((w: any) => {
        runningValue = w?.value + runningValue;
      });
      value = runningValue / working?.length;
      setSocial(parseFloat(value.toFixed(1)));
    } else setSocial("-");

    runningValue = 0;
    //Financial and Material Stability
    working = data.filter((i: any) => i.category === "Financial and Material Stability");
    if (working?.length > 0) {
      working?.map((w: any) => {
        runningValue = w?.value + runningValue;
      });
      value = runningValue / working?.length;
      setStability(parseFloat(value.toFixed(1)));
    } else setStability("-");
  };

  const calculateWellBeingTrend = (data: any[]) => {
    const groupedData: { [date: string]: { sum: number; count: number } } = data.reduce((acc: any, obj: any) => {
      const date = obj.assessment_at;
      const value = obj.value;
      if (!acc[date]) {
        acc[date] = { sum: 0, count: 0 };
      }
      acc[date].sum += value;
      acc[date].count++;
      return acc;
    }, {});

    // Calculate average for each group
    const averagedData = Object.entries(groupedData).map(([date, { sum, count }]) => ({
      date,
      score: sum / count,
    }));

    setTrend(averagedData);
    //return averagedData;
  };

  const downloadReport = async () => {
    setReportRunning(true);
    const reportSection = document.getElementById("wellbeing-section");
    const reportTitle = document.getElementById("wellbeing-title");

    if (reportSection) {
      if (reportTitle) {
        reportTitle.style.visibility = "hidden";
        reportTitle.style.display = "none";
      }

      const images = reportSection.getElementsByTagName("img");
      for (let i = 0; i < images.length; i++) {
        images[i].crossOrigin = "anonymous";
      }

      const canvas = await html2canvas(reportSection, { useCORS: true });
      setCanvas(canvas);

      if (reportTitle) {
        reportTitle.style.visibility = "visible";
        reportTitle.style.display = "flex";
      }
    } else {
      console.error("Report section not found");
      setReportRunning(false);
    }
  };

  const getCurrentDateTime = () => {
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hour = String(now.getHours()).padStart(2, "0");
    const minute = String(now.getMinutes()).padStart(2, "0");
    const second = String(now.getSeconds()).padStart(2, "0");

    return `${year}${month}${day}${hour}${minute}${second}`;
  };

  return (
    <div id="wellbeing-section">
      <Row id="wellbeing-title">
        <Col className="col-8">
          <h4 className="mb-0 app-header-text">Well Being Assessment</h4>
        </Col>
        <Col className="text-end col-4">
          {!iPad && (
            <Dropdown as={ButtonGroup}>
              <Dropdown.Toggle id="dropdown-custom-1" className="app-primary-color button">
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Downloads
              </Dropdown.Toggle>
              <Dropdown.Menu className="super-colors">
                <Dropdown.Item eventKey="1" onClick={() => downloadReport()}>
                  Report
                </Dropdown.Item>
                <Dropdown.Item
                  eventKey="2"
                  as={CSVLink}
                  data={fullTableData ? fullTableData : [{}]}
                  headers={fullTableHeaders}
                  filename={`wellbeing_${startDate?.replaceAll("-", "")}_to_${endDate?.replaceAll("-", "")}_${getCurrentDateTime()}.csv`}
                  target="_blank"
                >
                  Data Export
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </Col>
      </Row>
      <ReportTimeInterval
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        dateInterval={dateInterval}
        setDateInterval={setDateInterval}
        intervalOne={90}
        intervalOneText={"Last 3 Months"}
        intervalTwo={180}
        intervalTwoText={"Last 6 Months"}
        intervalThree={365}
        intervalThreeText={"Last Year"}
        intervalFour={10000}
        intervalFourText={"All Time"}
        onClickRefresh={getWellBeingData}
        hideDates={true}
      />
      <Row className="justify-content-center gap-3 mt-3">
        <Col className="col-5">
          <Card className="bg-light">
            <Card.Body>
              <Row className="text-center">
                <Col className="col-9 d-flex align-items-center">
                  <h5 className="app-orange-text">{"Happiness and Life Satisfaction"}</h5>
                </Col>
                <Col className="col-3 d-flex align-items-center">
                  <h2 className="text-muted text-end">{satisfaction}</h2>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col className="col-5">
          <Card className="bg-light">
            <Card.Body>
              <Row className="text-center">
                <Col className="col-9 d-flex align-items-center">
                  <h5 className="app-orange-text">{"Mental and Physical Health"}</h5>
                </Col>
                <Col className="col-3 d-flex align-items-center">
                  <h2 className="text-muted text-end">{health}</h2>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col className="col-5">
          <Card className="bg-light">
            <Card.Body>
              <Row className="text-center">
                <Col className="col-9 d-flex align-items-center">
                  <h5 className="app-orange-text">{"Meaning and Purpose"}</h5>
                </Col>
                <Col className="col-3 d-flex align-items-center">
                  <h2 className="text-muted text-end">{purpose}</h2>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col className="col-5">
          <Card className="bg-light">
            <Card.Body>
              <Row className="text-center">
                <Col className="col-9 d-flex align-items-center">
                  <h5 className="app-orange-text">{"Character and Virtue"}</h5>
                </Col>
                <Col className="col-3 d-flex align-items-center">
                  <h2 className="text-muted text-end">{virtue}</h2>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col className="col-5">
          <Card className="bg-light">
            <Card.Body>
              <Row className="text-center">
                <Col className="col-9 d-flex align-items-center">
                  <h5 className="app-orange-text">{"Close Social Relationships"}</h5>
                </Col>
                <Col className="col-3 d-flex align-items-center">
                  <h2 className="text-muted text-end">{social}</h2>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col className="col-5">
          <Card className="bg-light">
            <Card.Body>
              <Row className="text-center">
                <Col className="col-9 d-flex align-items-center">
                  <h5 className="app-orange-text">{"Financial and Material Stability"}</h5>
                </Col>
                <Col className="col-3 d-flex align-items-center">
                  <h2 className="text-muted text-end">{stability}</h2>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col></Col>
        <Col className="col-8">
          <WellBeingLineChart assessData={trend} />
        </Col>
        <Col></Col>
      </Row>
    </div>
  );
};

export default WellBeingTab;
